import { Entity } from '../../model/entity.model';

/**
 * A PENDING entity is waiting for the server to build its table, and the editor reads the entity list only once.
 * This follows each PENDING entity until the server moves it on, and hands over the entity it moved on to.
 */
export class PendingStatusWatch {
  private readonly following = new Map<string, () => void>();

  constructor(
    private readonly listen: (id: string, onDoc: (entity: Entity | undefined) => void) => () => void,
    private readonly onSettled: (entity: Entity) => void,
  ) {}

  /** Match the listeners to this list: exactly its PENDING entities are followed. */
  follow(list: Entity[]): void {
    const pending = new Set(list.filter((e) => e.status === 'PENDING').map((e) => e.id));
    for (const id of [...this.following.keys()]) if (!pending.has(id)) this.release(id);
    for (const id of pending) if (!this.following.has(id)) this.start(id);
  }

  stop(): void {
    for (const id of [...this.following.keys()]) this.release(id);
  }

  private start(id: string): void {
    // Set first: a listener may answer before listen() returns, and that answer must find something to release.
    this.following.set(id, () => {});
    let settledAtOnce = false;
    const unlisten = this.listen(id, (entity) => {
      if (entity?.status === 'PENDING') return;
      if (!this.following.has(id)) return;
      settledAtOnce = true;
      this.release(id);
      if (entity) this.onSettled(entity);
    });
    if (settledAtOnce) unlisten();
    else this.following.set(id, unlisten);
  }

  private release(id: string): void {
    this.following.get(id)?.();
    this.following.delete(id);
  }
}
